import { ROLES } from '@/core/security/roles';
import {
  DEMO_OTP,
  OTP_MAX_ATTEMPTS,
  OTP_RESEND_COOLDOWN_SECONDS,
  OTP_TTL_SECONDS,
} from '../constants';
import type {
  AuthTokens,
  OtpErrorCode,
  OtpSendResponse,
  UserProfile,
} from '../models';

/**
 * Mock accounts, shaped to the real API's payloads
 * (mdfiles/loginapi.md sections 1-3) so mock and live modes are
 * indistinguishable to everything upstream of the service layer.
 *
 * `mega_admin` / `rabiya@123` mirrors the credential pair in the document.
 */

interface MockAccount {
  password: string;
  tokens: AuthTokens;
  profile: UserProfile;
  /** Roles the app's authorization layer understands. */
  appRoles: string[];
}

const ACCESS_TOKEN_TTL_SECONDS = 900;

/**
 * What mock /users/me reports as `organizationCode`
 * (organization-setup-step1-apiflow.md section 2.1).
 *
 * Kept in step with the organization fixture's own seed
 * (organization-setup/api/mocks/organizationStep1.mock.ts) through the same
 * VITE_MOCK_ORG_STATE flag, rather than by importing across features. Only the
 * tenant-provisioning account has one; the others report null, which is the
 * documented "skip the organization GET" case.
 */
const MOCK_ORGANIZATION_CODE: string | null =
  import.meta.env.VITE_MOCK_ORG_STATE === 'new' ? null : 'ORG-014';

const ACCOUNTS: Record<string, MockAccount> = {
  mega_admin: {
    password: 'rabiya@123',
    tokens: {
      accessToken: 'mock_access_token_mega_admin',
      refreshToken: 'mock_refresh_token_mega_admin',
      expiresIn: ACCESS_TOKEN_TTL_SECONDS,
      tokenType: 'Bearer',
    },
    appRoles: [ROLES.ADMIN],
    profile: {
      id: 2,
      userName: 'mega_admin',
      statusId: 11,
      detailId: 2,
      role: { id: 3, name: 'Mega Admin' },
      organizationCode: MOCK_ORGANIZATION_CODE,
      /* Mega Admin holds no assignments -- the empty list is the real answer, not a gap. */
      entityDetail: [],
      salutation: 'Mr.',
      firstName: 'Mega',
      middleName: null,
      lastName: 'Admin',
      emailAddress: 'mega_admin@example.test',
      secondaryEmailAddress: 'mega_admin.alt@example.test',
      phoneCodeId: 7,
      phoneNumber: '9000000001',
      gender: 'Male',
      employeeCode: 'EMP0001',
      designationId: 7,
      jobCode: 'JOB-MA',
      jobTitle: 'Mega Administrator',
      dateOfBirth: '1985-01-10',
      dateOfJoining: '2024-01-01',
      userTypeId: 14,
      detailStatusId: 11,
      createdDate: '2026-08-28T17:22:00.03349',
      modifiedDate: '2026-08-28T17:22:00.03349',
      // The numeric FK stays null; organizationCode above is what the
      // wizard keys off (section 2.1).
      organizationId: null,
    },
  },
  initiator: {
    password: 'rabiya@123',
    tokens: {
      accessToken: 'mock_access_token_initiator',
      refreshToken: 'mock_refresh_token_initiator',
      expiresIn: ACCESS_TOKEN_TTL_SECONDS,
      tokenType: 'Bearer',
    },
    appRoles: [ROLES.INITIATOR],
    profile: {
      id: 3,
      userName: 'initiator',
      role: { id: 4, name: 'Initiator' },
      organizationCode: null,
      /* Two assignments, so the sidebar list renders plurally in mock mode. */
      entityDetail: [
        { id: 1, name: 'AIT Global Pvt Ltd', entityLogo: null },
        { id: 2, name: 'AIT Global Services', entityLogo: null },
      ],
      firstName: 'Ravi',
      lastName: 'Kulkarni',
      emailAddress: 'initiator@example.test',
      employeeCode: 'EMP0002',
      jobTitle: 'Procurement Initiator',
    },
  },
  supplier: {
    password: 'rabiya@123',
    tokens: {
      accessToken: 'mock_access_token_supplier',
      refreshToken: 'mock_refresh_token_supplier',
      expiresIn: ACCESS_TOKEN_TTL_SECONDS,
      tokenType: 'Bearer',
    },
    appRoles: [ROLES.SUPPLIER],
    profile: {
      id: 4,
      userName: 'supplier',
      role: { id: 5, name: 'Supplier' },
      organizationCode: null,
      firstName: 'Asha',
      lastName: 'Menon',
      emailAddress: 'supplier@example.test',
      employeeCode: 'EMP0003',
      jobTitle: 'Supplier Contact',
    },
  },
};

/** Accepts the account's Employee ID or email address -- or, as the server does for an account holding one, its username. */
function findAccount(identifier: string): MockAccount | null {
  const key = identifier.trim().toLowerCase();
  const direct = ACCOUNTS[key];
  if (direct) return direct;

  return (
    Object.values(ACCOUNTS).find(
      (account) =>
        (account.profile.emailAddress ?? '').toLowerCase() === key ||
        (account.profile.employeeCode ?? '').toLowerCase() === key,
    ) ?? null
  );
}

const BY_REFRESH_TOKEN: Record<string, MockAccount> = Object.fromEntries(
  Object.values(ACCOUNTS).map((account) => [account.tokens.refreshToken, account]),
);

export const MOCK_ACCOUNT_USERNAMES = Object.keys(ACCOUNTS);

/** Maps a mock profile onto the roles core/security understands. */
export function getMockAppRoles(username: string): string[] {
  return findAccount(username)?.appRoles ?? [];
}

export function getMockLoginResponse(
  username: string,
  password: string,
): AuthTokens | null {
  const account = findAccount(username);
  if (!account || account.password !== password) return null;
  return { ...account.tokens };
}

/** Rotation: a refresh yields a fresh access token and keeps the refresh one. */
export function getMockRefreshResponse(refreshToken: string): AuthTokens | null {
  const account = BY_REFRESH_TOKEN[refreshToken];
  if (!account) return null;
  return {
    ...account.tokens,
    accessToken: `${account.tokens.accessToken}_r${String(Date.now())}`,
  };
}

/**
 * Whether `password` is the mock account's current one, for the mock Change Password. An id no mock
 * account carries is not refused -- there is nothing to compare against.
 */
export function isMockCurrentPassword(userId: string, password: string): boolean {
  const account = Object.values(ACCOUNTS).find((entry) => String(entry.profile.id) === userId);
  return account === undefined || account.password === password;
}

export function getMockProfile(username?: string): UserProfile | null {
  if (!username) return null;
  const account = findAccount(username);
  return account ? { ...account.profile } : null;
}

/* -- OTP ------------------------------------------------------------------- */

interface MockOtpEntry {
  otp: string;
  expiresAt: number;
  attempts: number;
}

const otpStore = new Map<string, MockOtpEntry>();

/** Whether a mock user holds this email address -- the only identifier POST /auth/otp/request accepts. */
export function isMockRegisteredEmail(email: string): boolean {
  const key = email.trim().toLowerCase();
  return Object.values(ACCOUNTS).some(
    (account) => (account.profile.emailAddress ?? '').toLowerCase() === key,
  );
}

/**
 * NB-547: like the platform, an address no mock user holds is refused -- `null` here, which authService
 * turns into the platform's 400 VALIDATION_ERROR on `email` -- and no code is issued for it. A registered
 * address gets a code and the window.
 *
 * NB-548: like the platform, a request while the outstanding code is still valid (not expired, not locked)
 * re-sends that same code without extending it, and `expiresIn` is what it has left. Only an expired or locked
 * code is replaced.
 */
export function getMockSendOtpResponse(identifier: string): OtpSendResponse | null {
  if (!isMockRegisteredEmail(identifier)) return null;
  const key = identifier.trim().toLowerCase();
  const now = Date.now();
  let entry = otpStore.get(key);
  if (!entry || now >= entry.expiresAt || entry.attempts >= OTP_MAX_ATTEMPTS) {
    entry = { otp: DEMO_OTP, expiresAt: now + OTP_TTL_SECONDS * 1000, attempts: 0 };
    otpStore.set(key, entry);
  }
  return {
    success: true,
    message: 'OTP sent successfully',
    expiresIn: Math.max(1, Math.ceil((entry.expiresAt - now) / 1000)),
    resendAfter: OTP_RESEND_COOLDOWN_SECONDS,
  };
}

export const getMockResendOtpResponse = getMockSendOtpResponse;

export type MockVerifyResult =
  | { ok: true; data: AuthTokens }
  | { ok: false; code: OtpErrorCode };

/**
 * Discriminated rather than nullable, so the three documented OTP failures
 * each get their own message.
 */
export function getMockVerifyOtpResponse(
  identifier: string,
  otp: string,
): MockVerifyResult {
  const key = identifier.trim().toLowerCase();
  const entry = otpStore.get(key);

  if (!entry) return { ok: false, code: 'OTP_NOT_SENT' };

  if (Date.now() > entry.expiresAt) {
    otpStore.delete(key);
    return { ok: false, code: 'OTP_EXPIRED' };
  }

  if (entry.attempts >= OTP_MAX_ATTEMPTS) {
    return { ok: false, code: 'TOO_MANY_ATTEMPTS' };
  }

  if (entry.otp !== otp) {
    entry.attempts += 1;
    return {
      ok: false,
      code: entry.attempts >= OTP_MAX_ATTEMPTS ? 'TOO_MANY_ATTEMPTS' : 'INVALID_OTP',
    };
  }

  otpStore.delete(key);
  const account = findAccount(key);
  if (!account) return { ok: false, code: 'OTP_NOT_SENT' };
  return { ok: true, data: { ...account.tokens } };
}

/** Test-only reset so OTP state cannot leak between cases. */
export function resetMockOtpStore(): void {
  otpStore.clear();
}
